import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, CircularProgress } from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import { useSiteTakeoff } from '../SiteTakeoffContext';
import ModeSwitcher from './ModeSwitcher';
import TakeoffSelector from './TakeoffSelector';
import AreasMode from './modes/AreasMode';
import TakeoffMode from './modes/TakeoffMode';
import ProjectsMode from './modes/ProjectsMode';
import AreaEditor from '../editor/AreaEditor';

const useStyles = makeStyles(() => ({
  sidebar: {
    width: 360,
    minWidth: 360,
    background: '#ffffff',
    borderLeft: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  switcherWrap: {
    padding: '16px 16px 0 16px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
  },
  saveBar: {
    padding: '12px 16px',
    borderTop: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  saveBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
}));

const Sidebar = () => {
  const classes = useStyles();
  const ctx = useSiteTakeoff();
  const {
    url, activeMode, setActiveMode,
    editingAreaId, setEditingAreaId,
    editingShapeAreaId, startEditShape, stopEditShape,
    draggableAreaId, setDraggableAreaId,
    areas, jobsite,
    takeoffs, takeoff, saving, dirty,
    updateArea, deleteArea, duplicateArea,
    saveTakeoff,
    newTakeoff, switchTakeoff, deleteTakeoff, renameTakeoff,
    startDraw, snapping, setSnapping,
    handleDrawExclusion,
    legacyAreas, hasLegacyAreas, importLegacyAreas,
    zoomToArea, overlayManager,
    setBulkEditOpen,
    hiddenAreaIds, toggleAreaVisibility,
  } = ctx;

  const editingArea = editingAreaId
    ? areas.find(a => (a._id || a.id) === editingAreaId)
    : null;

  const handleEditAreaClose = () => {
    stopEditShape();
    setEditingAreaId(null);
    setDraggableAreaId(null);
  };

  const renderSaveBar = () => {
    if (!jobsite?._id) return null;
    const label = saving ? 'Saving…' : dirty ? 'Save Takeoff' : 'All saved';
    return (
      <div className={classes.saveBar}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="small"
          className={classes.saveBtn}
          data-tour="site-takeoff-save-button"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={() => saveTakeoff(takeoff?.name)}
          disabled={saving || !dirty}
        >
          {label}
        </Button>
      </div>
    );
  };

  if (editingArea) {
    const isEditingShape = editingShapeAreaId === editingAreaId;
    return (
      <div className={classes.sidebar}>
        <AreaEditor
          area={editingArea}
          areas={areas}
          onClose={handleEditAreaClose}
          onUpdate={(updates) => updateArea(editingAreaId, updates)}
          onDelete={() => { deleteArea(editingAreaId); handleEditAreaClose(); }}
          onDuplicate={(groupOptions) => {
            const newId = duplicateArea(editingAreaId, groupOptions);
            if (newId) {
              setTimeout(() => {
                setEditingAreaId(newId);
                setDraggableAreaId(newId);
              }, 50);
            }
          }}
          isEditingShape={isEditingShape}
          onStartEditShape={() => startEditShape(editingAreaId)}
          onStopEditShape={stopEditShape}
          onDrawExclusion={handleDrawExclusion}
          onEditArea={setEditingAreaId}
          isDragging={draggableAreaId === editingAreaId}
          onStopDrag={() => setDraggableAreaId(null)}
        />
        {renderSaveBar()}
      </div>
    );
  }

  return (
    <div className={classes.sidebar}>
      {jobsite?._id && (
        <div data-tour="site-takeoff-selector">
          <TakeoffSelector
            takeoffs={takeoffs}
            activeTakeoffId={takeoff?._id}
            activeTakeoffName={takeoff?.name || ''}
            onSwitchTakeoff={(id) => { switchTakeoff(id); setEditingAreaId(null); }}
            onNewTakeoff={() => { newTakeoff(); setEditingAreaId(null); }}
            onDeleteTakeoff={(id) => { deleteTakeoff(id); setEditingAreaId(null); }}
            onRenameTakeoff={renameTakeoff}
            disabled={saving}
          />
        </div>
      )}

      <div className={classes.switcherWrap} data-tour="site-takeoff-mode-switcher">
        <ModeSwitcher activeMode={activeMode} onModeChange={setActiveMode} />
      </div>

      <div className={classes.content}>
        {activeMode === 'areas' && (
          <AreasMode
            jobsite={jobsite}
            areas={areas}
            onDrawArea={(tool) => startDraw(tool)}
            snapping={snapping}
            onToggleSnapping={() => setSnapping(v => !v)}
            onEditArea={setEditingAreaId}
            onZoomToArea={zoomToArea}
            hasLegacyAreas={hasLegacyAreas}
            legacyAreaCount={(legacyAreas || []).length}
            onImportLegacyAreas={async () => {
              const name = window.prompt('Name for imported takeoff:', 'Imported Areas');
              if (!name) return;
              await importLegacyAreas(legacyAreas, name);
              setEditingAreaId(null);
            }}
            saving={saving}
            onBulkEdit={() => setBulkEditOpen(true)}
            hiddenAreaIds={hiddenAreaIds}
            onToggleVisibility={toggleAreaVisibility}
          />
        )}
        {activeMode === 'takeoff' && (
          <TakeoffMode
            areas={areas}
            rateOverrides={takeoff?.productionRates || {}}
          />
        )}
        {activeMode === 'projects' && (
          <ProjectsMode
            url={url}
            jobsite={jobsite}
            overlays={overlayManager?.overlays || []}
            activeOverlayId={overlayManager?.activeOverlayId}
            uploading={overlayManager?.uploading}
            savedOverlayUrls={overlayManager?.savedOverlayUrls || []}
            onUploadFile={overlayManager?.uploadOverlay}
            onAddExistingOverlay={overlayManager?.addExistingOverlay}
            onSetVisible={overlayManager?.setOverlayVisible}
            onSetOpacity={overlayManager?.setOverlayOpacity}
            onSetMasterOpacity={overlayManager?.setMasterOpacity}
            onSetLocked={overlayManager?.setOverlayLocked}
            onSetCategory={overlayManager?.setOverlayCategory}
            onActivate={overlayManager?.activateOverlay}
            onRemove={overlayManager?.removeOverlay}
          />
        )}
      </div>

      {renderSaveBar()}
    </div>
  );
};

export default Sidebar;
